// src/components/kiosk/KioskClaim.jsx
//
// /kiosk/claim/:code - opened by scanning the QR a new kiosk shows once it
// has Wi-Fi. The person (the facility's ambassador, its owner, or an admin)
// picks the facility; the kiosk then fetches its facility ID and device key
// by itself within seconds. Backed by /api/kiosk-claim (backend
// routes/kioskClaim.ts), which decides who may equip what.
//
// An ambassador's phone also sends its position: on site, next to the
// facility, that approves it at once (backend utils/kioskAutoApprove.ts).
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container, Typography, Paper, Stack, Button, TextField, Alert, CircularProgress, Box, Radio, Chip,
} from '@mui/material';
import useRoleAuth from '../../utils/useRoleAuth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const ROLES = ['ROLE_USER', 'ROLE_SALES', 'ROLE_ADMIN'];

const ERRORS = {
  CODE_EXPIRED: 'Ce code a expiré. La borne en affiche un nouveau : scannez-le à nouveau.',
  CODE_NOT_FOUND: 'Code inconnu. Scannez le QR affiché sur la borne.',
  INVALID_CODE: 'Code invalide. Scannez le QR affiché sur la borne.',
  ALREADY_ASSOCIATED: 'Cette borne est déjà associée à un établissement.',
  FACILITY_NOT_YOURS: 'Vous ne pouvez pas équiper cet établissement.',
};
const errorText = (code) => ERRORS[code] ?? 'Une erreur est survenue, réessayez.';

/** The phone's position, or null if refused / unavailable within 15 s. */
const getPosition = () => new Promise((resolve) => {
  if (!navigator.geolocation) { resolve(null); return; }
  navigator.geolocation.getCurrentPosition(
    (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy }),
    () => resolve(null), { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 });
});

/** What the ambassador reads about the facility's approval after associating. */
const APPROVAL = {
  APPROVED: ['success', 'Vous êtes sur place : l’établissement est validé, il apparaît maintenant sur QRProofs.'],
  NO_POSITION: ['info', 'Position non disponible : l’établissement sera validé par un administrateur. Autorisez la localisation pour le valider vous-même sur place.'],
  IMPRECISE_POSITION: ['info', 'Position trop imprécise : l’établissement sera validé par un administrateur.'],
  TOO_FAR: ['info', 'Vous semblez loin de l’établissement : il sera validé par un administrateur.'],
  NO_FACILITY_POSITION: ['info', 'L’établissement n’a pas encore de position : il sera validé par un administrateur.'],
};
async function call(method, path, token, body) {
  const r = await fetch(`${API_BASE_URL}/kiosk-claim${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `HTTP_${r.status}`);
  return data;
}

export default function KioskClaim() {
  const { code } = useParams();
  const { ready, forbidden, loading, getToken } = useRoleAuth(ROLES);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const load = useCallback((search = '') => {
    if (!ready) return;
    call('GET', `/${encodeURIComponent(code)}${search ? `?q=${encodeURIComponent(search)}` : ''}`, getToken())
      .then((d) => { setData(d); setError(''); })
      .catch((e) => setError(errorText(e.message)));
  }, [ready, getToken, code]);
  useEffect(() => { load(); }, [load]);

  const associate = async () => {
    if (selected.hasKiosk && !window.confirm(`${selected.name} a déjà une borne. En associant celle-ci, l'ancienne cessera de fonctionner. Continuer ?`)) return;
    setBusy(true); setError('');
    try {
      const position = data.ambassador ? await getPosition() : null;
      setDone(await call('POST', `/${encodeURIComponent(code)}`, getToken(),
        { facilityId: selected.id, ...(position ? { position } : {}) }));
    } catch (e) { setError(errorText(e.message)); } finally { setBusy(false); }
  };

  if (loading || (ready && !data && !error)) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (forbidden) return <Container sx={{ py: 4 }}><Alert severity="error">Connectez-vous avec un compte QRProofs.</Alert></Container>;

  if (done) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="success" sx={{ mb: 2 }}>Borne associée à <b>{done.facilityName}</b>.</Alert>
        {APPROVAL[done.autoApproval] && (
          <Alert severity={APPROVAL[done.autoApproval][0]} sx={{ mb: 2 }}>{APPROVAL[done.autoApproval][1]}</Alert>
        )}
        <Typography>
          La borne va s’activer d’elle-même dans quelques secondes : elle affiche « Borne prête », puis l’écran de notation.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>Associer une borne</Typography>
      {data && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Borne …{data.kiosk.id}{data.kiosk.firmware ? ` · firmware ${data.kiosk.firmware}` : ''}
        </Typography>
      )}
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
      {data?.kiosk.movingFrom && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Cette borne est associée à <b>{data.kiosk.movingFrom}</b> : elle passera à l’établissement choisi,
          et cessera de fonctionner pour {data.kiosk.movingFrom}.
        </Alert>
      )}
      {data && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Pour quel établissement ?</Typography>
          {(data.admin || data.facilities.length > 8) && (
            <TextField fullWidth size="small" sx={{ mb: 1.5 }} label="Rechercher (nom ou numéro)" value={q}
              onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') load(q.trim()); }}
              InputProps={{ endAdornment: <Button size="small" onClick={() => load(q.trim())}>OK</Button> }} />
          )}
          {data.facilities.length === 0 && (
            <Alert severity="info">Aucun établissement que vous pouvez équiper{q ? ' pour cette recherche' : ''}.</Alert>
          )}
          <Stack spacing={1}>
            {data.facilities.map((f) => (
              <Paper key={f.id} variant="outlined" onClick={() => setSelected(f)}
                sx={{ p: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, cursor: 'pointer',
                  borderColor: selected?.id === f.id ? '#0b4dff' : undefined, borderWidth: selected?.id === f.id ? 2 : 1 }}>
                <Radio checked={selected?.id === f.id} inputProps={{ 'aria-label': f.name }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 600 }} noWrap>{f.name}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>{f.address} · #{f.id}</Typography>
                </Box>
                {f.hasKiosk && <Chip size="small" label="a déjà une borne" />}
                {f.mineWithoutKiosk && <Chip size="small" color="primary" label="sans borne" />}
                {/* The chosen facility's own button: no scrolling down a long list to confirm. */}
                {selected?.id === f.id && (
                  <Button variant="contained" color="success" disabled={busy} aria-label={`Associer à ${f.name}`}
                    onClick={(e) => { e.stopPropagation(); associate(); }}
                    sx={{ ml: 'auto', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {busy ? (data.ambassador ? 'Localisation…' : 'Association…') : 'Associer'}
                  </Button>
                )}
              </Paper>
            ))}
          </Stack>
          {!selected && data.facilities.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              Touchez l’établissement, puis « Associer ».
            </Typography>
          )}
        </>
      )}
    </Container>
  );
}
